import {
  type CustomCell,
  type CustomRenderer,
  getMiddleCenterBias,
  GridCellKind,
  type Theme,
} from "@glideapps/glide-data-grid";

import {getDataGridShiftBadgeColors} from "../dataGridTheme";

export type DispatchSeparatorCellProps = {
  readonly kind: "dispatch-separator-cell";
  readonly title: string;
  readonly shiftStatus?: string;
  readonly equipment?: string;
  readonly shiftWindow?: string;
  readonly colorScheme?: "light" | "dark";
};

export type DispatchSeparatorCell = CustomCell<DispatchSeparatorCellProps>;

const drawPill = (
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  centerY: number,
  theme: Theme,
  color: {bg: string; text: string; border?: string}
): number => {
  const horizontalPadding = 8;
  const height = 20;
  const radius = 10;
  const width = Math.ceil(ctx.measureText(text).width) + horizontalPadding * 2;
  const y = centerY - height / 2;

  ctx.beginPath();
  ctx.roundRect(x, y, width, height, radius);
  ctx.fillStyle = color.bg;
  ctx.fill();

  if (color.border) {
    ctx.strokeStyle = color.border;
    ctx.stroke();
  }

  ctx.fillStyle = color.text;
  ctx.fillText(text, x + horizontalPadding, centerY + getMiddleCenterBias(ctx, theme));

  return x + width + 6;
};

export const DispatchSeparatorCellRenderer: CustomRenderer<DispatchSeparatorCell> = {
  kind: GridCellKind.Custom,
  isMatch: (cell: CustomCell): cell is DispatchSeparatorCell =>
    (cell.data as Partial<DispatchSeparatorCellProps>).kind === "dispatch-separator-cell",
  draw: (args, cell) => {
    const {ctx, rect, theme} = args;
    const {title, shiftStatus, equipment, shiftWindow, colorScheme} = cell.data;
    const centerY = rect.y + rect.height / 2;
    const xPad = theme.cellHorizontalPadding;
    let x = rect.x + xPad;

    ctx.save();

    ctx.font = "600 13px system-ui, -apple-system, BlinkMacSystemFont, Segoe UI, sans-serif";
    ctx.fillStyle = theme.textDark;
    ctx.fillText(title, x, centerY + getMiddleCenterBias(ctx, theme));
    x += Math.ceil(ctx.measureText(title).width) + 12;

    if (shiftStatus) {
      const badgeText = [shiftStatus, shiftWindow, equipment].filter(Boolean).join(" | ");

      ctx.font = "600 11px system-ui, -apple-system, BlinkMacSystemFont, Segoe UI, sans-serif";
      drawPill(ctx, badgeText, x, centerY, theme, getDataGridShiftBadgeColors(shiftStatus, colorScheme));
    }

    ctx.restore();

    return true;
  },
  provideEditor: undefined,
};
